import React from 'react';
import AiIndicator from './AiIndicator';

// GH-messaging-grouping: onStarToggle(conversation) is optional. When present
// each row renders a star icon that fires the callback (Messaging.js hits the
// backend). Backward-compat: omit the prop → star hidden, list looks identical.
function ConversationList({ conversations, selectedId, onSelect, loading, onStarToggle, pending = [], selectedPendingId = null, onSelectPending }) {
  const pendingRows = pending.length > 0 && (
    <div style={styles.pendingBlock}>
      <div style={styles.pendingHead}>Unidentified senders <span style={styles.pendingCount}>{pending.length}</span></div>
      {pending.map(p => (
        <div key={`p-${p.id}`} style={{ ...styles.pendingRow, ...(selectedPendingId === p.id ? styles.pendingRowSelected : {}) }} onClick={() => onSelectPending?.(p)}>
          <div style={styles.pendingAvatar}>?</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={styles.pendingPhone}>{formatPhoneShort(p.from_phone)}<span style={styles.timestamp}>{p.created_at ? formatRelativeTime(p.created_at) : ''}</span></div>
            <div style={styles.pendingPreview}>{p.content}</div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingIcon}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        Loading conversations...
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div style={styles.list}>
        {pendingRows}
        <div style={styles.empty}>
        <div style={styles.emptyIcon}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <p style={styles.emptyText}>No conversations yet</p>
        <p style={styles.emptySubtext}>Start a new conversation with a patient</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.list}>
      {pendingRows}
      {conversations.map(conversation => (
        <ConversationItem
          key={conversation.id}
          conversation={conversation}
          isSelected={selectedId === conversation.id}
          onClick={() => onSelect(conversation)}
          onStarToggle={onStarToggle}
        />
      ))}
    </div>
  );
}

function ConversationItem({ conversation, isSelected, onClick, onStarToggle }) {
  const hasUnread = conversation.unread_count > 0;
  const lastMessageTime = conversation.last_message_at
    ? formatRelativeTime(conversation.last_message_at)
    : '';

  // Generate initials from name
  const initials = getInitials(conversation.first_name, conversation.last_name);

  // Check if this is an urgent/priority conversation (you can add logic based on your data)
  const isUrgent = conversation.priority === 'urgent';

  return (
    <div
      style={{
        ...styles.item,
        ...(isSelected ? styles.itemSelected : {}),
        ...(hasUnread && !isSelected ? styles.itemUnread : {})
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = '#f9fafb';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected && !hasUnread) {
          e.currentTarget.style.backgroundColor = 'transparent';
        } else if (!isSelected && hasUnread) {
          e.currentTarget.style.backgroundColor = '#fefce8';
        }
      }}
    >
      <div style={styles.itemContent}>
        {/* Avatar */}
        <div style={styles.avatar}>
          <span style={styles.avatarText}>{initials}</span>
        </div>

        {/* Main content */}
        <div style={styles.mainContent}>
          {/* Header row */}
          <div style={styles.itemHeader}>
            <div style={styles.nameRow}>
              <span style={{
                ...styles.patientName,
                ...(hasUnread ? styles.patientNameUnread : {})
              }}>
                {conversation.first_name} {conversation.last_name}
              </span>
              {isUrgent && (
                <span style={styles.urgentBadge}>Urgent</span>
              )}
              {conversation.open_complaint && (
                <span style={styles.urgentBadge} title="Patient complaint waiting in the AI Queue">Complaint</span>
              )}
              <AiIndicator
                aiEnabled={conversation.ai_enabled}
                aiTakeoverStatus={conversation.ai_takeover_status}
                needsHumanAttention={conversation.needs_human_attention}
              />
            </div>
            <span style={styles.timestamp}>{lastMessageTime}</span>
            {onStarToggle && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onStarToggle(conversation); }}
                title={conversation.starred ? 'Unstar' : 'Star'}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: '15px', padding: '0 4px', color: conversation.starred ? '#f59e0b' : '#d1d5db',
                }}
                aria-label={conversation.starred ? 'Unstar conversation' : 'Star conversation'}
              >
                {conversation.starred ? '★' : '☆'}
              </button>
            )}
          </div>

          {/* Phone number */}
          <div style={styles.phone}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            {conversation.patient_phone}
          </div>

          {/* Message preview */}
          <div style={{
            ...styles.preview,
            ...(hasUnread ? styles.previewUnread : {}),
            ...(!conversation.last_message_preview ? { fontStyle: 'italic', color: '#9ca3af' } : {})
          }}>
            {conversation.last_message_preview
              ? truncate(conversation.last_message_preview, 50)
              : 'No messages yet'}
          </div>
        </div>

        {/* Unread badge */}
        {hasUnread && (
          <div style={styles.unreadBadge}>
            {conversation.unread_count}
          </div>
        )}
      </div>
    </div>
  );
}

// Utility functions
function getInitials(firstName, lastName) {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return first + last || '?';
}

function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

function truncate(str, maxLength) {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
}

function formatPhoneShort(p) {
  const d = String(p || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || '';
}

const styles = {
  pendingBlock: { borderBottom: '2px solid #FDE68A', background: '#FFFBEB' },
  pendingHead: { padding: '8px 16px 4px', fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#92400E', display: 'flex', alignItems: 'center', gap: 6 },
  pendingCount: { background: '#F59E0B', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10 },
  pendingRow: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 16px', cursor: 'pointer', borderTop: '1px solid #FDE68A' },
  pendingRowSelected: { background: '#FEF3C7' },
  pendingAvatar: { width: 36, height: 36, borderRadius: '50%', background: '#F59E0B', color: '#fff', fontWeight: 800, display: 'grid', placeItems: 'center', flexShrink: 0 },
  pendingPhone: { display: 'flex', justifyContent: 'space-between', gap: 8, fontWeight: 700, fontSize: 13, color: '#78350F', fontVariantNumeric: 'tabular-nums' },
  pendingPreview: { fontSize: 12.5, color: '#92400E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 },
  list: {
    flex: 1,
    overflowY: 'auto'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    color: '#6b7280',
    fontSize: '14px'
  },
  loadingIcon: {
    marginBottom: '12px',
    animation: 'pulse 2s infinite'
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center'
  },
  emptyIcon: {
    marginBottom: '12px'
  },
  emptyText: {
    color: '#374151',
    fontSize: '15px',
    fontWeight: '500',
    margin: 0
  },
  emptySubtext: {
    color: '#9ca3af',
    fontSize: '13px',
    margin: '4px 0 0 0'
  },
  item: {
    padding: '16px',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  itemSelected: {
    backgroundColor: '#eff6ff',
    borderLeft: '4px solid #3b82f6'
  },
  itemUnread: {
    backgroundColor: '#fefce8'
  },
  itemContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#dbeafe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  avatarText: {
    color: '#1d4ed8',
    fontSize: '14px',
    fontWeight: '600'
  },
  mainContent: {
    flex: 1,
    minWidth: 0
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '2px'
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  patientName: {
    fontWeight: '500',
    color: '#111827',
    fontSize: '14px'
  },
  patientNameUnread: {
    fontWeight: '600'
  },
  urgentBadge: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    fontSize: '10px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  timestamp: {
    fontSize: '12px',
    color: '#9ca3af',
    flexShrink: 0
  },
  phone: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '4px'
  },
  preview: {
    fontSize: '13px',
    color: '#6b7280',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  previewUnread: {
    color: '#374151',
    fontWeight: '500'
  },
  unreadBadge: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '12px',
    minWidth: '20px',
    textAlign: 'center',
    flexShrink: 0
  }
};

export default ConversationList;
